import React, { useState, useEffect, useCallback } from "react";

const KEYBOARD_ROWS = [
  ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P", "Å"],
  ["A", "S", "D", "F", "G", "H", "J", "K", "L", "Ø", "Æ"],
  ["ENTER", "Z", "X", "C", "V", "B", "N", "M", "⌫"],
];

function evaluateGuess(guess, solution) {
  const result = Array(guess.length).fill("absent");
  const solutionChars = solution.split("");
  const guessChars = guess.split("");

  // First pass: mark correct (green)
  for (let i = 0; i < guessChars.length; i++) {
    if (guessChars[i] === solutionChars[i]) {
      result[i] = "correct";
      solutionChars[i] = null;
      guessChars[i] = null;
    }
  }

  // Second pass: mark present (yellow)
  for (let i = 0; i < guessChars.length; i++) {
    if (guessChars[i] === null) continue;
    const idx = solutionChars.indexOf(guessChars[i]);
    if (idx !== -1) {
      result[i] = "present";
      solutionChars[idx] = null;
    }
  }

  return result;
}

const TILE_COLORS = {
  correct: "bg-green-600 text-white border-green-600",
  present: "bg-yellow-500 text-white border-yellow-500",
  absent: "bg-gray-500 text-white border-gray-500",
  empty: "bg-white border-gray-300",
  active: "bg-white border-gray-800",
};

const KEY_COLORS = {
  correct: "bg-green-600 text-white",
  present: "bg-yellow-500 text-white",
  absent: "bg-gray-500 text-white",
  unused: "bg-gray-200 text-gray-800",
};

const MAX_GUESSES = 6;

const WordleGame = ({
  solution,
  onClose,
  guesses,
  setGuesses,
  currentGuess,
  setCurrentGuess,
  gameOver,
  setGameOver,
  won,
  setWon,
}) => {
  const upperSolution = solution.toUpperCase();
  const wordLength = upperSolution.length;

  const [shake, setShake] = useState(false);
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState("");

  const showMessage = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(""), 1500);
  };

  const checkWord = async (word) => {
    try {
      const res = await fetch(
        `https://ord.uib.no/api/suggest?q=${encodeURIComponent(word.toLowerCase())}&dict=bm&n=1&include=e&dform=int`
      );
      const data = await res.json();
      return data.cnt > 0;
    } catch {
      // If the API is down, allow the guess
      return true;
    }
  };

  const letterStates = useCallback(() => {
    const states = {};
    const priority = { correct: 3, present: 2, absent: 1 };
    for (const guess of guesses) {
      const evaluation = evaluateGuess(guess, upperSolution);
      for (let i = 0; i < guess.length; i++) {
        const letter = guess[i];
        const state = evaluation[i];
        if (!states[letter] || priority[state] > priority[states[letter]]) {
          states[letter] = state;
        }
      }
    }
    return states;
  }, [guesses, upperSolution]);

  const handleKey = useCallback(
    async (key) => {
      if (gameOver || checking) return;

      if (key === "ENTER") {
        if (currentGuess.length !== wordLength) {
          setShake(true);
          setTimeout(() => setShake(false), 500);
          return;
        }

        // Skip dictionary check if the guess matches the solution
        if (currentGuess !== upperSolution) {
          setChecking(true);
          const valid = await checkWord(currentGuess);
          setChecking(false);
          if (!valid) {
            showMessage("Ikke et gyldig ord");
            setShake(true);
            setTimeout(() => setShake(false), 500);
            return;
          }
        }

        const newGuesses = [...guesses, currentGuess];
        setGuesses(newGuesses);
        setCurrentGuess("");

        if (currentGuess === upperSolution) {
          setWon(true);
          setGameOver(true);
        } else if (newGuesses.length >= MAX_GUESSES) {
          setGameOver(true);
        }
        return;
      }

      if (key === "⌫" || key === "BACKSPACE") {
        setCurrentGuess((prev) => prev.slice(0, -1));
        return;
      }

      if (currentGuess.length < wordLength && /^[A-ZÆØÅ]$/.test(key)) {
        setCurrentGuess((prev) => prev + key);
      }
    },
    [currentGuess, gameOver, checking, guesses, upperSolution, wordLength, setCurrentGuess, setGameOver, setGuesses, setWon]
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const key = e.key.toUpperCase();
      if (key === "ENTER") handleKey("ENTER");
      else if (key === "BACKSPACE") handleKey("⌫");
      else if (/^[A-ZÆØÅ]$/.test(key)) handleKey(key);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKey]);

  const renderGrid = () => {
    const rows = [];
    for (let r = 0; r < MAX_GUESSES; r++) {
      const cells = [];
      const isCurrentRow = r === guesses.length && !gameOver;
      const guess = guesses[r];

      for (let c = 0; c < wordLength; c++) {
        let letter = "";
        let colorClass = TILE_COLORS.empty;

        if (guess) {
          letter = guess[c];
          const evaluation = evaluateGuess(guess, upperSolution);
          colorClass = TILE_COLORS[evaluation[c]];
        } else if (isCurrentRow && c < currentGuess.length) {
          letter = currentGuess[c];
          colorClass = TILE_COLORS.active;
        }

        cells.push(
          <div
            key={c}
            className={`w-12 h-12 sm:w-14 sm:h-14 border-2 flex items-center justify-center text-xl sm:text-2xl font-bold rounded ${colorClass} transition-colors duration-300 select-none`}
          >
            {letter}
          </div>
        );
      }

      rows.push(
        <div
          key={r}
          className={`flex gap-1.5 justify-center ${isCurrentRow && shake ? "animate-shake" : ""}`}
        >
          {cells}
        </div>
      );
    }
    return rows;
  };

  const renderKeyboard = () => {
    const states = letterStates();
    return KEYBOARD_ROWS.map((row, ri) => (
      <div key={ri} className="flex gap-1 justify-center mb-1">
        {row.map((key) => {
          const isSpecial = key === "ENTER" || key === "⌫";
          const colorClass = states[key]
            ? KEY_COLORS[states[key]]
            : KEY_COLORS.unused;

          return (
            <button
              key={key}
              onClick={() => handleKey(key)}
              className={`${isSpecial ? "px-2 sm:px-4 text-xs sm:text-sm" : "w-8 sm:w-10 text-sm sm:text-base"} h-12 sm:h-14 rounded font-bold ${colorClass} hover:opacity-80 transition-all duration-150 select-none`}
            >
              {key}
            </button>
          );
        })}
      </div>
    ));
  };

  return (
    <div className="flex flex-col items-center gap-4 p-4 select-none">
      <div className="flex items-center justify-between w-full max-w-sm">
        <h2 className="text-2xl font-bold text-gray-800">🥚 Wordle</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-700 text-2xl font-bold leading-none"
        >
          ✕
        </button>
      </div>

      <div className="h-6 flex items-center justify-center">
        {message && (
          <div className="bg-gray-800 text-white px-4 py-1 rounded-lg text-sm font-semibold shadow-lg whitespace-nowrap">
            {message}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1.5">{renderGrid()}</div>

      {gameOver && (
        <div className="text-center py-2">
          {won ? (
            <p className="text-xl font-bold text-green-600">
              Bra jobbet! 🎉 Neste hint: Ekornet ser på bokstavene.
            </p>
          ) : (
            <p className="text-lg text-gray-700">
              Godt forsøk! For svar, spør {" "}
              <span className="font-bold text-advent-primary-dark">
                {"ESTEN"}
              </span>
            </p>
          )}
          <button
            onClick={onClose}
            className="mt-3 px-6 py-2 bg-advent-primary hover:bg-advent-primary-dark text-white rounded-lg font-semibold transition-all"
          >
            Lukk
          </button>
        </div>
      )}

      {!gameOver && <div className="mt-2">{renderKeyboard()}</div>}
    </div>
  );
};

export default WordleGame;
